"use client";

import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { sk, type Messages } from "@/lib/messages/sk";
import type { Locale } from "@/lib/i18n";

/**
 * Locale + messages provider.
 *
 * Server layout reads user.prefs.locale, calls getMessages(locale), and
 * wraps the tree in <LocaleProvider locale={..} messages={..}>.
 *
 * Client components read via useT() (messages) or useLocale() (current code).
 */

type Ctx = {
  locale: Locale;
  t: Messages;
};

const LocaleContext = createContext<Ctx>({ locale: "sk", t: sk });

export function LocaleProvider({
  locale,
  messages,
  children,
}: {
  locale: Locale;
  messages: Messages;
  children: ReactNode;
}) {
  return (
    <LocaleContext.Provider value={{ locale, t: messages }}>
      {children}
    </LocaleContext.Provider>
  );
}

/** Get the current messages bundle in a client component. */
export function useT(): Messages {
  return useContext(LocaleContext).t;
}

/** Get the current locale code (sk | en). */
export function useLocale(): Locale {
  return useContext(LocaleContext).locale;
}
